import { DomainError, OTHER_COUNTRY, PLATE_COUNTRY_CODES, defaultPlateCountry, formatPlate, odometerWarning, validateVehicle, vehicleFieldProblems, type VehicleInput } from "@szerviz/core";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { ErrorText } from "../components/ErrorText";
import { Field, TextInput } from "../components/Field";
import { Help } from "../components/Help";
import { VehicleDataSheet } from "../components/VehicleDataSheet";
import { DueServices } from "../components/DueServices";
import { Recommendations } from "../components/Recommendations";
import { VehiclePhoto } from "../components/VehiclePhoto";
import { VehicleReminderFields } from "../components/Reminders";
import { Photos } from "../components/Photos";
import { Plate } from "../components/Plate";
import { MissingNote, ProblemNote, useRequired } from "../components/Required";
import { useApp, useFeature } from "../context";
import { VehicleWorkOrders } from "./WorkOrders";
import type { Customer, Vehicle, VehiclePhotoResult } from "../data";

export function Vehicles({ openId }: { openId?: string }) {
  const { t } = useTranslation();
  const { store, workshop, go, back } = useApp();
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState<Vehicle[] | null>(null);
  const [editing, setEditingState] = useState<Vehicle | "new" | null>(null);
  const setEditing = (v: Vehicle | "new" | null) => go("vehicles", v === null ? undefined : v === "new" ? "new" : v.id);

  // The address decides what is open (#vehicles/<id>), so the customer page can link here and back.
  useEffect(() => {
    if (!openId) {
      setEditingState(null);
      return;
    }
    if (openId === "new") {
      setEditingState("new");
      return;
    }
    let alive = true;
    void store.getVehicle(openId).then((v) => alive && setEditingState(v));
    return () => {
      alive = false;
    };
  }, [openId, store]);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(async () => {
    try {
      setRows(await store.searchVehicles(workshop.id, query));
    } catch (e) {
      setError(e);
    }
  }, [store, workshop.id, query]);

  useEffect(() => {
    const h = setTimeout(() => void load(), 200);
    return () => clearTimeout(h);
  }, [load]);

  if (editing) {
    return (
      <section>
        <div className="page-head">
          <button type="button" className="link" onClick={() => back("vehicles")}>
            ← {t("common.back")}
          </button>
          <h1>{editing === "new" ? t("vehicles.new") : <Plate plate={editing.plateNumber} country={editing.plateCountry} size="lg" />}</h1>
        </div>
        {editing !== "new" && editing.customerId && (
          <a
            className="owner-band"
            href={`#customers/${editing.customerId}`}
            onClick={(e) => {
              e.preventDefault();
              go("customers", editing.customerId!);
            }}
          >
            <span>
              <small>{t("vehicle.owner")}</small>
              <strong>{editing.customerName}</strong>
            </span>
            <span className="owner-go">{t("vehicle.openOwner")} →</span>
          </a>
        )}
        {editing !== "new" && <VehicleWorkOrders vehicleId={editing.id} />}
        {editing !== "new" && <Recommendations vehicleId={editing.id} />}
        {editing !== "new" && <Photos vehicleId={editing.id} defaultKind="document" />}
        <VehicleForm
          key={editing === "new" ? "new" : editing.id}
          initial={editing === "new" ? { plateNumber: "" } : editing}
          id={editing === "new" ? undefined : editing.id}
          onDone={() => {
            setEditing(null);
            void load();
          }}
        />
        {editing !== "new" && (
          <VehicleDataSheet
            key={`${editing.id}-${editing.typeId ?? ""}`}
            vehicle={editing}
            onChanged={() => void store.getVehicle(editing.id).then((v) => v && setEditingState(v))}
          />
        )}
      </section>
    );
  }

  return (
    <section>
      <div className="page-head">
        <h1>
          {t("vehicles.title")} <Help id="vehicles.page" />
        </h1>
        <button type="button" className="primary" onClick={() => setEditing("new")}>
          {t("vehicles.new")}
        </button>
      </div>
      <input
        className="search"
        type="search"
        placeholder={t("vehicles.searchPlaceholder")}
        aria-label={t("common.search")}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <ErrorText error={error} />
      {rows === null ? (
        <p>{t("common.loading")}</p>
      ) : rows.length === 0 ? (
        <p className="muted">{t("common.noResults")}</p>
      ) : (
        <>
          <p className="muted">{t("common.results", { count: String(rows.length) })}</p>
          <table className="list">
            <thead>
              <tr>
                <th>{t("vehicle.plateNumber")}</th>
                <th>{t("vehicle.make")}</th>
                <th>{t("vehicle.model")}</th>
                <th>{t("vehicle.engineCode")}</th>
                <th>{t("vehicle.owner")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((v) => (
                <tr key={v.id} onClick={() => setEditing(v)} tabIndex={0} onKeyDown={(e) => e.key === "Enter" && setEditing(v)}>
                  <td>
                    <Plate plate={v.plateNumber} country={v.plateCountry} size="sm" />
                  </td>
                  <td>{v.make}</td>
                  <td>{v.model}</td>
                  <td>{v.engineCode}</td>
                  <td>{v.customerName ?? <span className="muted">{t("vehicle.noOwner")}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}

const num = (s: string): number | null => {
  const n = Number(s.replace(",", "."));
  return s.trim() === "" || !Number.isFinite(n) ? null : Math.round(n);
};

export function VehicleForm({ initial, id, onDone, photo }: { initial: VehicleInput; id?: string; onDone: (saved?: Vehicle) => void; photo?: VehiclePhotoResult | null }) {
  const { t } = useTranslation();
  const { store, workshop } = useApp();
  const { i18n } = useTranslation();
  const remindersOn = useFeature("reminders");
  const ownCountry = defaultPlateCountry(workshop.language);
  const [form, setForm] = useState<VehicleInput>(() => ({ ...initial, plateCountry: initial.plateCountry ?? ownCountry }));
  const regionName = (code: string) => {
    try {
      return new Intl.DisplayNames([i18n.language], { type: "region" }).of(code) ?? code;
    } catch {
      return code;
    }
  };
  const [ownerQuery, setOwnerQuery] = useState("");
  const [owners, setOwners] = useState<Customer[]>([]);
  const [ownerName, setOwnerName] = useState<string | null>((initial as Vehicle).customerName ?? null);
  const [error, setError] = useState<unknown>(null);
  const [touched, setTouched] = useState(!!id);
  const req = useRequired("vehicle", form, touched);
  // only typed numbers are checked; a stored one is kept until it changes (#110)
  const problems = vehicleFieldProblems(form, id ? initial : undefined);
  const bad = (field: string) => problems.some((p) => p.field === field);
  // a lower reading than the stored one only warns, saving stays possible (#120)
  const storedKm = id ? (initial.odometerKm ?? null) : null;
  const kmLower = !bad("odometerKm") && odometerWarning(form.odometerKm ?? null, storedKm) !== null;

  useEffect(() => {
    if (!ownerQuery.trim()) {
      setOwners([]);
      return;
    }
    const h = setTimeout(() => void store.searchCustomers(workshop.id, ownerQuery, 8).then(setOwners), 200);
    return () => clearTimeout(h);
  }, [ownerQuery, store, workshop.id]);

  const set = (key: keyof VehicleInput, transform?: (s: string) => unknown) => (v: string) => {
    setTouched(true);
    setForm((f) => ({ ...f, [key]: transform ? transform(v) : v }));
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      // A VIN that was already stored (e.g. imported) is not re-checked unless it changes.
      if (req.missing.length > 0) throw new DomainError("REQUIRED_MISSING");
      if (problems.length > 0) return;
      const strictVin = !id || form.vin !== initial.vin;
      const v = validateVehicle(form, { strictVin });
      const saved = await store.saveVehicle(workshop.id, { ...v, plateNumber: formatPlate(v.plateNumber, v.plateCountry) }, id);
      onDone(saved);
    } catch (err) {
      setError(err);
    }
  };

  const remove = async () => {
    if (!id || !globalThis.confirm?.(t("common.confirmDelete"))) return;
    try {
      await store.deleteVehicle(id);
      onDone();
    } catch (err) {
      setError(err);
    }
  };

  return (
    <form className="form-grid" onSubmit={save} noValidate>
      {!id && (
        <VehiclePhoto
          preset={photo}
          onRead={({ inspectionValidUntil, ...fields }) => {
            setTouched(true);
            // the inspection date only where the reminders use it (never guessed: only when printed)
            setForm((f) => ({ ...f, ...fields, ...(remindersOn && inspectionValidUntil ? { inspectionValidUntil } : {}) }));
          }}
          onOwner={(c) => {
            setForm((f) => ({ ...f, customerId: c.id }));
            setOwnerName(c.name);
          }}
        />
      )}
      <TextInput label={t("vehicle.plateNumber")} value={form.plateNumber} onChange={set("plateNumber")} autoFocus={!id} required invalid={req.missingField("plateNumber")} help="vehicle.plateNumber" />
      <Field label={t("vehicle.plateCountry")}>
        <select value={form.plateCountry ?? ""} aria-label={t("vehicle.plateCountry")} onChange={(e) => set("plateCountry")(e.target.value)}>
          {PLATE_COUNTRY_CODES.map((c) => (
            <option key={c} value={c}>
              {regionName(c)}
            </option>
          ))}
          <option value={OTHER_COUNTRY}>{t("vehicle.plateCountryOther")}</option>
        </select>
      </Field>
      <TextInput label={t("vehicle.vin")} value={form.vin} onChange={set("vin")} required={req.isReq("vin")} invalid={req.missingField("vin") || bad("vin")} help="vehicle.vin" />
      <TextInput label={t("vehicle.make")} value={form.make} onChange={set("make")} required={req.isReq("make")} invalid={req.missingField("make")} />
      <TextInput label={t("vehicle.model")} value={form.model} onChange={set("model")} required={req.isReq("model")} invalid={req.missingField("model")} />
      <TextInput label={t("vehicle.engineCode")} value={form.engineCode} onChange={set("engineCode")} required={req.isReq("engineCode")} invalid={req.missingField("engineCode")} help="vehicle.engineCode" />
      <TextInput label={t("vehicle.powerKw")} value={form.powerKw} onChange={set("powerKw", num)} type="number" help="vehicle.powerKw" invalid={bad("powerKw")} />
      <TextInput label={t("vehicle.modelYear")} value={form.modelYear} onChange={set("modelYear", num)} type="number" invalid={bad("modelYear")} />
      <TextInput label={t("vehicle.fuelType")} value={form.fuelType} onChange={set("fuelType")} />
      <TextInput label={t("vehicle.tyreSize")} value={form.tyreSize} onChange={set("tyreSize")} help="vehicle.tyreSize" />
      <TextInput label={t("vehicle.odometerKm")} value={form.odometerKm} onChange={set("odometerKm", num)} type="number" required={req.isReq("odometerKm")} invalid={req.missingField("odometerKm") || bad("odometerKm")} />
      {kmLower && (
        <p className="warn wide" role="status">
          {t("workOrder.odometerLower", { last: (storedKm ?? 0).toLocaleString(i18n.language) })}
        </p>
      )}
      {id && <DueServices vehicle={initial as Vehicle} km={form.odometerKm ?? null} />}
      <VehicleReminderFields
        form={form}
        onChange={(patch) => {
          setTouched(true);
          setForm((f) => ({ ...f, ...patch }));
        }}
      />
      <TextInput label={t("vehicle.typeDescription")} value={form.typeDescription} onChange={set("typeDescription")} wide />
      <Field label={t("vehicle.owner")} wide required={req.isReq("customerId")} invalid={req.missingField("customerId")}>
        <div className="owner">
          <span>{ownerName ?? (form.customerId ? "" : t("vehicle.noOwner"))}</span>
          <input
            type="search"
            placeholder={t("customers.searchPlaceholder")}
            aria-label={t("vehicle.owner")}
            value={ownerQuery}
            onChange={(e) => setOwnerQuery(e.target.value)}
          />
          {owners.length > 0 && (
            <ul className="suggest">
              {owners.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setTouched(true);
                      setForm((f) => ({ ...f, customerId: c.id }));
                      setOwnerName(c.name);
                      setOwnerQuery("");
                    }}
                  >
                    {c.name} {c.city ? `(${c.city})` : ""}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Field>
      <Field label={t("vehicle.notes")} wide>
        <textarea value={form.notes ?? ""} onChange={(e) => set("notes")(e.target.value)} aria-label={t("vehicle.notes")} rows={2} />
      </Field>
      <div className="actions wide">
        <ErrorText error={error} />
        <button type="submit" className="primary" disabled={req.missing.length > 0 || problems.length > 0}>
          {t("common.save")}
        </button>
        <MissingNote missing={req.missing} />
        <ProblemNote problems={problems} />
        {id && (
          <button type="button" className="danger" onClick={remove}>
            {t("common.delete")}
          </button>
        )}
      </div>
    </form>
  );
}
